import { supabase } from '@/lib/supabase';

export const ACCEPTED_EXTRACT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AcceptedMediaType = (typeof ACCEPTED_EXTRACT_TYPES)[number];

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

export type ExtractFromImageParams = {
  image_base64: string;
  media_type: AcceptedMediaType;
  api_key?: string;
};

export type ExtractErrorCode =
  | 'IMAGE_TOO_LARGE'
  | 'IMAGE_NOT_RECOGNIZED'
  | 'API_ERROR'
  | 'EMPTY_RESPONSE'
  | 'INVALID_BODY'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NETWORK_ERROR';

export class ExtractError extends Error {
  constructor(
    public readonly code: ExtractErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ExtractError';
  }
}

export type ExtractResult = {
  extracted_text: string;
  meta: { model: string; inputTokens: number; outputTokens: number };
};

export async function extractFromImage(params: ExtractFromImageParams): Promise<ExtractResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new ExtractError('UNAUTHORIZED', 'Session expirée. Reconnecte-toi.');
  }

  const { data, error } = await supabase.functions.invoke('extract-exercise-image', {
    body: params,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });

  if (error) {
    console.error('[extract-exercise-image] network/invoke error:', error.message);
    throw new ExtractError('NETWORK_ERROR', error.message);
  }

  if (data?.error) {
    console.error('[extract-exercise-image] server error:', data.error, data.message);
    throw new ExtractError(data.error as ExtractErrorCode, data.message ?? "Erreur d'extraction");
  }

  if (!data?.extracted_text) {
    console.error('[extract-exercise-image] empty output:', data);
    throw new ExtractError('EMPTY_RESPONSE', 'Réponse vide du serveur');
  }

  return data as ExtractResult;
}

export function extractErrorMessage(code: ExtractErrorCode): string {
  switch (code) {
    case 'IMAGE_TOO_LARGE':
      return "L'image est trop grande (maximum 5 Mo).";
    case 'IMAGE_NOT_RECOGNIZED':
      return "Aucun exercice détecté dans l'image. Essaie avec une photo plus nette ou cadre mieux l'exercice.";
    case 'API_ERROR':
      return "Erreur de communication avec l'IA. Vérifie ta clé API ou réessaie.";
    case 'EMPTY_RESPONSE':
      return "L'IA n'a pas renvoyé de réponse. Réessaie.";
    case 'INVALID_BODY':
      return 'Format de requête invalide.';
    case 'NETWORK_ERROR':
      return 'Erreur réseau. Vérifie ta connexion et réessaie.';
    case 'UNAUTHORIZED':
    case 'FORBIDDEN':
      return "Tu n'as pas les droits pour utiliser cette fonctionnalité.";
    default:
      return 'Erreur inattendue. Réessaie.';
  }
}

export function validateImageFile(file: File): string | null {
  if (!ACCEPTED_EXTRACT_TYPES.includes(file.type as AcceptedMediaType)) {
    return 'Format non supporté. Utilise JPEG, PNG ou WebP.';
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return "L'image ne doit pas dépasser 5 Mo.";
  }
  return null;
}

/**
 * Concatène les textes extraits de plusieurs images en un seul énoncé.
 * Filtre les chaînes vides (ou uniquement des espaces), `trim()` chacune, et
 * joint par une ligne vide (`\n\n`). L'ordre du tableau est préservé (page 1,
 * page 2, …). Pure → unit-testable.
 */
export function joinExtractedStatements(texts: string[]): string {
  return texts
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .join('\n\n');
}

/** Accepte tout `Blob` (donc aussi un `File`, ou le résultat d'un `fetch(...).blob()`). */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Lecture du fichier échouée'));
    reader.readAsDataURL(file);
  });
}
